import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { go, restoreScroll, useRoute } from './hooks';
import { ToastHost } from './ui';
import { SavedHost } from './confirm';
import { UpdateBanner } from './update';
import { FlyerScreen } from './recommend';
import { StatsScreen } from './stats';
import { TrashScreen } from './trash';
import { ApiaryForm, ApiaryScreen, HomeScreen } from './screens/apiaries';
import { ColonyForm, ColonyHistoryScreen, ColonyScreen, QueenChangeForm, SplitForm, type ColonySection } from './screens/colonies';
import { EventForm } from './screens/event-form';
import { LabelsScreen, ProductForm, SettingsScreen, TreatmentLogScreen } from './screens/records';
import { ScannerScreen } from './screens/scanner';
import { ApiaryYearScreen, ColonyYearScreen } from './screens/yearly';
import { FeedbackInboxScreen, FeedbackScreen } from './feedback';
import { AnnualReportScreen, CountReportScreen, FeedingScreen, ReportsHub, SeasonCalendarScreen, VarroaOverviewScreen, WinteringScreen } from './screens/reports';
import { Icon, type IconName } from './icons';
import { isWriteRoute, LicenceIssueScreen, LicenceScreen, LockedScreen, useLicence } from './licence';
import { canWrite } from '../lib/licence';

function resolve(parts: string[], query: URLSearchParams): { view: ReactNode; tab?: string } {
  const [a, b, c] = parts;
  switch (a) {
    case undefined:
      return { view: <HomeScreen />, tab: '' };
    case 'stanoviste':
      if (b === 'nove') return { view: <ApiaryForm /> };
      if (c === 'upravit') return { view: <ApiaryForm id={b} /> };
      if (c === 'rok') return { view: <ApiaryYearScreen id={b} /> };
      return { view: <ApiaryScreen id={b} /> };
    case 'v': // krátký odkaz z QR štítku
    case 'vcelstvo':
      if (b === 'nove') return { view: <ColonyForm apiaryId={query.get('stanoviste') ?? undefined} /> };
      if (c === 'upravit') return { view: <ColonyForm key={parts[3] ?? ''} id={b} section={parts[3] as ColonySection | undefined} /> };
      if (c === 'matka') return { view: <QueenChangeForm colonyId={b} /> };
      if (c === 'oddelek') return { view: <SplitForm colonyId={b} /> };
      if (c === 'historie') return { view: <ColonyHistoryScreen id={b} /> };
      if (c === 'rok') return { view: <ColonyYearScreen key={query.get('r') ?? ''} id={b} /> };
      return { view: <ColonyScreen id={b} /> };
    case 'zapis':
      return { view: <EventForm key={parts.join('/') + query} id={b === 'novy' ? undefined : b} /> };
    case 'prehledy':
      return { view: <ReportsHub />, tab: 'prehledy' };
    case 'evidence':
      return { view: <TreatmentLogScreen />, tab: 'prehledy' };
    case 'pripominky':
      return { view: b === 'schranka' ? <FeedbackInboxScreen /> : b === 'statistiky' ? <StatsScreen /> : <FeedbackScreen key={query.toString()} /> };
    case 'varroaza':
      return { view: <VarroaOverviewScreen />, tab: 'prehledy' };
    case 'kalendar':
      return { view: <SeasonCalendarScreen />, tab: 'prehledy' };
    case 'zakrmeni':
      return { view: <FeedingScreen />, tab: 'prehledy' };
    case 'zimovani':
      return { view: <WinteringScreen />, tab: 'prehledy' };
    case 'hlaseni':
      return { view: <CountReportScreen />, tab: 'prehledy' };
    case 'zprava':
      return { view: <AnnualReportScreen />, tab: 'prehledy' };
    case 'nastaveni':
      return { view: <SettingsScreen />, tab: 'nastaveni' };
    case 'pripravek':
      return { view: <ProductForm id={b === 'novy' ? undefined : b} /> };
    case 'skenovat':
      return { view: <ScannerScreen />, tab: 'skenovat' };
    case 'stitky':
      return { view: <LabelsScreen apiaryId={b} /> };
    case 'kos':
      return { view: <TrashScreen />, tab: 'nastaveni' };
    case 'letak':
      return { view: <FlyerScreen />, tab: 'nastaveni' };
    case 'licence':
      return { view: b === 'vydat' ? <LicenceIssueScreen /> : <LicenceScreen />, tab: 'nastaveni' };
  }
  return { view: <HomeScreen />, tab: '' };
}

const TABS: [string, IconName, string][] = [
  ['', 'hive', 'Včelnice'],
  ['skenovat', 'qr', 'Načíst QR'],
  ['prehledy', 'chart', 'Přehledy'],
  ['nastaveni', 'gear', 'Nastavení'],
];

export function App() {
  const { parts, query } = useRoute();
  const hash = location.hash;
  useEffect(() => restoreScroll(hash), [hash]);
  const lic = useLicence();
  const locked = !!lic && !canWrite(lic.status) && isWriteRoute(parts);
  const { view, tab } = locked ? { view: <LockedScreen />, tab: undefined } : resolve(parts, query);
  return (
    <div className={'app' + (tab !== undefined ? ' has-tabs' : '')}>
      {view}
      {tab !== undefined && (
        <nav className="tabs no-print">
          {TABS.map(([path, icon, label]) => (
            <button key={path} className={'tab' + (tab === path ? ' on' : '')} onClick={() => go(path, true)}>
              <span className="tab-icon">
                <Icon name={icon} size={26} />
              </span>
              {label}
            </button>
          ))}
        </nav>
      )}
      <UpdateBanner />
      <ToastHost />
      <SavedHost />
    </div>
  );
}
